// ============================================================
// CUSTOMER INVOICE ATTACHMENTS TYPES
// ============================================================

/**
 * Type of customer invoice attachment
 */
export type CustomerInvoiceAttachmentType =
  | 'quote'
  | 'signed_quote'
  | 'contract'
  | 'signed_contract'
  | 'change_order'
  | 'invoice'
  | 'other';

/**
 * Attachment type options for UI selectors
 */
export const CUSTOMER_INVOICE_ATTACHMENT_TYPES: {
  value: CustomerInvoiceAttachmentType;
  label: string;
}[] = [
  { value: 'quote', label: 'Quote' },
  { value: 'signed_quote', label: 'Signed Quote' },
  { value: 'contract', label: 'Contract' },
  { value: 'signed_contract', label: 'Signed Contract' },
  { value: 'change_order', label: 'Change Order' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'other', label: 'Other' },
];

/**
 * Get attachment type label
 */
export function getAttachmentTypeLabel(type: CustomerInvoiceAttachmentType): string {
  return CUSTOMER_INVOICE_ATTACHMENT_TYPES.find((t) => t.value === type)?.label ?? type;
}

/**
 * Base customer invoice attachment record from the database
 */
export interface CustomerInvoiceAttachment {
  id: string;
  businessId: string;
  invoiceId: string | null;
  projectId: string;
  uploadedBy: string;
  fileName: string;
  filePath: string;
  fileSizeBytes: number | null;
  mimeType: string | null;
  attachmentType: CustomerInvoiceAttachmentType;
  tags: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Customer invoice attachment from view with joined context
 */
export interface CustomerInvoiceAttachmentView extends CustomerInvoiceAttachment {
  // Invoice context
  invoiceNumber: string | null;
  invoiceStatus: string | null;
  invoiceTotal: number | null;
  // Project context
  projectName: string;
  projectStatus: string;
  // Customer context
  customerFirstName: string | null;
  customerLastName: string | null;
  customerName: string;
  // Uploader context
  uploadedByName: string | null;
}

// ============================================================
// UPLOAD TYPES
// ============================================================

/**
 * Status of a file during the upload process
 */
export type AttachmentUploadStatus =
  | 'pending'
  | 'ready'
  | 'uploading'
  | 'uploaded'
  | 'error';

/**
 * An attachment file in the upload queue
 */
export interface AttachmentFile {
  id: string;
  file: File;
  preview: string | null;
  fileType: 'image' | 'pdf';
  status: AttachmentUploadStatus;
  error?: string;
  uploadProgress?: number;
  attachmentType: CustomerInvoiceAttachmentType;
  notes?: string;
}

/**
 * Parameters for uploading an attachment
 */
export interface UploadAttachmentParams {
  file: File;
  businessId: string;
  projectId: string;
  uploadedBy: string;
  attachmentType: CustomerInvoiceAttachmentType;
  notes?: string;
}

/**
 * Parameters for updating an attachment
 */
export interface UpdateAttachmentParams {
  id: string;
  attachmentType?: CustomerInvoiceAttachmentType;
  notes?: string;
}

/**
 * Filter options for querying attachments
 */
export interface AttachmentFilters {
  projectId?: string;
  attachmentType?: CustomerInvoiceAttachmentType;
}

// ============================================================
// MAPPING HELPERS
// ============================================================

/**
 * Map database row to CustomerInvoiceAttachment type
 */
export function mapAttachmentFromDb(row: Record<string, unknown>): CustomerInvoiceAttachment {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    invoiceId: row.invoice_id as string | null,
    projectId: row.project_id as string,
    uploadedBy: row.uploaded_by as string,
    fileName: row.file_name as string,
    filePath: row.file_path as string,
    fileSizeBytes: row.file_size_bytes as number | null,
    mimeType: row.mime_type as string | null,
    attachmentType: row.attachment_type as CustomerInvoiceAttachmentType,
    tags: (row.tags as string[]) || [],
    notes: row.notes as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

/**
 * Map database row to CustomerInvoiceAttachmentView type
 */
export function mapAttachmentViewFromDb(row: Record<string, unknown>): CustomerInvoiceAttachmentView {
  return {
    ...mapAttachmentFromDb(row),
    invoiceNumber: row.invoice_number as string | null,
    invoiceStatus: row.invoice_status as string | null,
    invoiceTotal: row.invoice_total as number | null,
    projectName: row.project_name as string,
    projectStatus: row.project_status as string,
    customerFirstName: row.customer_first_name as string | null,
    customerLastName: row.customer_last_name as string | null,
    customerName: row.customer_name as string,
    uploadedByName: row.uploaded_by_name as string | null,
  };
}

// ============================================================
// UTILITY TYPES & FUNCTIONS
// ============================================================

/**
 * Supported file types for attachment upload
 */
export const ATTACHMENT_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/pdf',
] as const;

/**
 * File type to accept string for input elements
 */
export const ATTACHMENT_ACCEPT_STRING =
  '.jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf';

/**
 * Check if a file is a supported attachment type
 */
export function isValidAttachmentType(file: File): boolean {
  return ATTACHMENT_ACCEPTED_TYPES.includes(file.type as typeof ATTACHMENT_ACCEPTED_TYPES[number]);
}

/**
 * Determine the file type category
 */
export function getFileTypeCategory(file: File): 'image' | 'pdf' {
  if (file.type === 'application/pdf') return 'pdf';
  return 'image';
}

/**
 * Check if a file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

/**
 * Check if a file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Generate a unique ID for an attachment file in the upload queue
 */
export function generateAttachmentId(): string {
  return `attach_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Attachment type display configuration
 */
export const ATTACHMENT_TYPE_CONFIG: Record<CustomerInvoiceAttachmentType, { label: string; color: string }> = {
  quote: { label: 'Quote', color: 'bg-blue-100 text-blue-700' },
  signed_quote: { label: 'Signed Quote', color: 'bg-green-100 text-green-700' },
  contract: { label: 'Contract', color: 'bg-purple-100 text-purple-700' },
  signed_contract: { label: 'Signed Contract', color: 'bg-green-100 text-green-700' },
  change_order: { label: 'Change Order', color: 'bg-orange-100 text-orange-700' },
  invoice: { label: 'Invoice', color: 'bg-yellow-100 text-yellow-700' },
  other: { label: 'Other', color: 'bg-secondary-100 text-secondary-700' },
};
